import { useContext, useEffect, useMemo } from 'react';
import { isEmpty } from 'lodash';
import queryString from 'query-string';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { notFoundPath } from 'common/config/urls';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import LayoutContext from 'context/LayoutContext';
import { WORKBOARD_CURRENT_JOBS_URL } from 'dashboard409a/common/config/urls';
import { DASHBOARD_WORKBOARD_SLUG } from 'dashboard409a/common/constants';
import {
  CURRENT_JOBS_SLUG,
  CURRENT_MODELS_SLUG,
  INFORMATION_SLUG,
  OPERATIONS_SLUG,
  OPPORTUNITIES_SLUG,
  OVERVIEW_SLUG,
  PAST_JOBS_SLUG,
  PAST_MODELS_SLUG,
  PREVIOUS_REPORTS_SLUG,
  SETTINGS_SLUG,
  SUBSCRIPTION_SLUG,
} from 'dashboard409a/common/constants/urls';
import {
  Operations,
  Overview,
  Settings,
  Subscription,
  WorkboardCurrentJobs,
  WorkboardOpportunities,
  WorkboardPastJobs,
} from 'dashboard409a/pages';
import { getCompanyDetails } from 'dashboard409a/states/features/company';
import { updateGlobalState } from 'dashboard409a/states/features/global';
import { findValuationScalarOpinion, getValuationScalarOpinion } from 'dashboard409a/states/features/valuation';
import { AppDispatchType, RootStateType } from 'dashboard409a/states/store';
import { clearPreviousWorkboardRoute, getPreviousWorkboardRoute } from 'dashboard409a/utilities';
import InformationData from '../Information/InformationData';
import WorkboardCurrentModels from '../Workboard/WorkboardCurrentModels/WorkboardCurrentModels';
import WorkboardPastModels from '../Workboard/WorkboardPastModels/WorkboardPastModels';

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { companySlug, tableSlugParam, tabSlugParam } = useParams();
  const companies = useSelector((state: RootStateType) => state.user.standalone_companies_permissions);
  const dispatch = useDispatch<AppDispatchType>();
  const { currentCompanyMeasurementDateId } = useSelector((state: RootStateType) => state.global);

  const [{ user }] = useStore() as unknown as UseStoreValues;

  // Determine if we're in Workboard mode (no companySlug, but tabSlugParam exists)
  const isWorkboard = !companySlug && tabSlugParam;
  const currentSlug = isWorkboard ? tabSlugParam : tableSlugParam;

  const { setPageTitle, setNavItems, setShowAlert, setAlertMessage } = useContext(LayoutContext);

  const currentCompany = useMemo(
    () => companies?.find(company => company.slug === companySlug) || null,
    [companies, companySlug]
  );

  const userIsStaff = useMemo(() => {
    if (user) {
      return user.is_staff;
    }
    return false;
  }, [user]);

  const parsedQuery = useMemo(() => queryString.parse(location.search), [location]);

  const menuItems = useMemo(() => {
    const navigateToUrl = (slug: string) => {
      if (isWorkboard) {
        const newPathName = `/dashboard/analyst/${slug}`;
        navigate({ pathname: newPathName, search: queryString.stringify(parsedQuery) });
      } else if (companySlug) {
        const newPathName = `/dashboard/companies/${companySlug}/${slug}`;
        navigate({ pathname: newPathName, search: queryString.stringify(parsedQuery) });
      }
    };

    if (isWorkboard) {
      // Workboard menu items
      return [
        {
          callback: () => navigateToUrl(CURRENT_JOBS_SLUG),
          id: 'current-jobs',
          itemKey: 0,
          slug: CURRENT_JOBS_SLUG,
          title: 'Current Jobs',
        },
        {
          callback: () => navigateToUrl(PAST_JOBS_SLUG),
          id: 'past-jobs',
          itemKey: 1,
          slug: PAST_JOBS_SLUG,
          title: 'Past Jobs',
        },
        {
          callback: () => navigateToUrl(OPPORTUNITIES_SLUG),
          id: 'opportunities',
          itemKey: 2,
          slug: OPPORTUNITIES_SLUG,
          title: 'Opportunities',
        },
        {
          callback: () => navigateToUrl(CURRENT_MODELS_SLUG),
          id: 'current-models',
          itemKey: 3,
          slug: CURRENT_MODELS_SLUG,
          title: 'Current Models',
        },
        {
          callback: () => navigateToUrl(PAST_MODELS_SLUG),
          id: 'past-models',
          itemKey: 4,
          slug: PAST_MODELS_SLUG,
          title: 'Past Models',
        },
      ];
    }

    // Company dashboard menu items
    return [
      {
        callback: () => navigateToUrl(OVERVIEW_SLUG),
        id: 'overview',
        itemKey: 0,
        slug: OVERVIEW_SLUG,
        title: 'Overview',
      },
      {
        callback: () => navigateToUrl(PREVIOUS_REPORTS_SLUG),
        id: 'previous-reports',
        itemKey: 1,
        slug: PREVIOUS_REPORTS_SLUG,
        title: 'Previous Reports',
      },
      ...(userIsStaff
        ? [
          {
            callback: () => navigateToUrl(INFORMATION_SLUG),
            id: 'information',
            itemKey: 2,
            slug: INFORMATION_SLUG,
            title: 'Information',
          },
          {
            callback: () => navigateToUrl(SETTINGS_SLUG),
            id: 'settings',
            itemKey: 3,
            slug: SETTINGS_SLUG,
            title: 'Settings',
          },
          {
            callback: () => navigateToUrl(SUBSCRIPTION_SLUG),
            id: 'subscription',
            itemKey: 4,
            slug: SUBSCRIPTION_SLUG,
            title: 'Subscription',
          },
        ]
        : []),
    ];
  }, [isWorkboard, companySlug, userIsStaff, parsedQuery, navigate]);

  // Hide alert box when browsing to another location
  useEffect(
    () => () => {
      setShowAlert?.(false);
      setAlertMessage?.('');
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [location.pathname]
  );

  useEffect(() => {
    if (currentCompany?.id) {
      dispatch(updateGlobalState({ currentCompany }));
      dispatch(getValuationScalarOpinion(currentCompany?.id));
      dispatch(getCompanyDetails(currentCompany?.id));
    }

    if (currentCompanyMeasurementDateId) {
      dispatch(findValuationScalarOpinion(currentCompanyMeasurementDateId));
    }
  }, [currentCompany, currentCompanyMeasurementDateId, dispatch]);

  useEffect(() => {
    if (!isEmpty(currentCompany) && currentCompany.name) {
      setPageTitle?.(currentCompany.name);
      setNavItems?.(menuItems);
    }

    return () => {
      setPageTitle?.(null);
      setNavItems?.(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany, location, menuItems]);

  // Handle navigation redirects in useEffect to avoid setState during render
  useEffect(() => {
    if (isWorkboard) {
      // Workboard validation
      const validAnalystSlugs = [
        CURRENT_JOBS_SLUG,
        PAST_JOBS_SLUG,
        OPPORTUNITIES_SLUG,
        CURRENT_MODELS_SLUG,
        PAST_MODELS_SLUG,
      ];

      if (currentSlug && !validAnalystSlugs.includes(currentSlug)) {
        // Check if there's a saved workboard route in localStorage
        const savedRoute = getPreviousWorkboardRoute();
        if (savedRoute && savedRoute.includes(DASHBOARD_WORKBOARD_SLUG)) {
          // Navigate to the saved route and clear it
          navigate(savedRoute, { replace: true });
          clearPreviousWorkboardRoute();
        } else {
          // Default to current jobs
          navigate(WORKBOARD_CURRENT_JOBS_URL, { replace: true });
        }
      } else if (currentSlug === CURRENT_JOBS_SLUG) {
        // When loading current-jobs, check if there's a saved route
        const savedRoute = getPreviousWorkboardRoute();
        if (savedRoute && savedRoute.includes(DASHBOARD_WORKBOARD_SLUG) && savedRoute !== location.pathname) {
          // Navigate to the saved route and clear it
          navigate(savedRoute, { replace: true });
          clearPreviousWorkboardRoute();
        }
      }
    } else if (!companySlug) {
      navigate(notFoundPath, { replace: true });
    } else {
      // Company dashboard validation
      const validCompanySlugs = [
        OVERVIEW_SLUG,
        PREVIOUS_REPORTS_SLUG,
        INFORMATION_SLUG,
        OPERATIONS_SLUG,
        SETTINGS_SLUG,
        SUBSCRIPTION_SLUG,
      ];

      if (currentSlug && !validCompanySlugs.includes(currentSlug)) {
        navigate(`/dashboard/companies/${companySlug}/${OVERVIEW_SLUG}`, { replace: true });
      }
    }
  }, [isWorkboard, companySlug, currentSlug, navigate, location.pathname]);

  const renderCurrentView = () => {
    switch (currentSlug) {
      // Company dashboard views
      case OVERVIEW_SLUG:
      case PREVIOUS_REPORTS_SLUG:
        return <Overview />;
      case INFORMATION_SLUG:
        return userIsStaff ? <InformationData /> : <></>;
      case OPERATIONS_SLUG:
        return <Operations />;
      case SETTINGS_SLUG:
        return userIsStaff ? <Settings /> : <></>;
      case SUBSCRIPTION_SLUG:
        return userIsStaff ? <Subscription /> : <></>;
      case CURRENT_JOBS_SLUG:
        return <WorkboardCurrentJobs />;
      case PAST_JOBS_SLUG:
        return <WorkboardPastJobs />;
      case OPPORTUNITIES_SLUG:
        return <WorkboardOpportunities />;
      case CURRENT_MODELS_SLUG:
        return <WorkboardCurrentModels />;
      case PAST_MODELS_SLUG:
        return <WorkboardPastModels />;
      
      default:
        return <></>;
    }
  };

  // For Workboard, we don't need companySlug
  if (!isWorkboard && !companySlug) {
    return null;
  }

  return <div style={{ width: '100%' }}>{renderCurrentView()}</div>;
};

export default Dashboard;
